/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import moment from "moment";
import { Row } from "react-bootstrap";
import Card from "components/molecules/Card/Card";
import { ReactComponent as EyeIcon } from "assets/images/icons/Eye.svg";
import usePermissions from "hooks/usePermissions";
import DataTable from "components/organisms/DataTable/DataTable";
import { getQuotations } from "api";
import { useIntl } from "react-intl";
import { useColumnOrder, useExpanded, useSortBy, useTable } from "react-table";
import errorToast from "helpers/errorToast";
import { GlobalState, useStateMachine } from "little-state-machine";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import PropTypes from "prop-types";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import ProviderInviteStatus from "constants/ProviderInviteStatus";
import getQuotesColumns from "./quotesColumns";
import SubQuote from "./SubQuote";
import "./quotes.scss";
import RateCardErrors from "../RateCardErrors";
import ParcelQuoteErrors from "../ParcelQuoteErrors";
import { IProviderProps, IQuotation } from "./QuotesType";
import QuoteError from "./QuoteError";

interface IQuotesProps {
  id: string;
  requestStatus: string;
  fetchData: () => void;
  providers?: any;
  pullQuotesRefetchInterval?: number | false;
  isQuotation?: boolean;
  setIsQuotation: any;
  setIsInitialize: any;
  fallback: any;
}

interface ITrafficLightBucket {
  [freightMode: string]: {
    totalCostUSD: number;
    totalQuotes: number;
    averageCostUSD?: number;
    upperMarginUSD?: number;
    lowerMarginUSD?: number;
  };
}

const Quotes = function ({
  id,
  requestStatus,
  fetchData,
  providers,
  pullQuotesRefetchInterval = false,
  isQuotation,
  setIsQuotation,
  setIsInitialize,
  fallback,
}: IQuotesProps) {
  const { actions } = useStateMachine({ updateQuotationsAvailable }) as any;
  const intl = useIntl();
  const [showAlternativeQuotes, setShowAlternativeQuotes] = useState(false);
  const [isProcessing, setIsProcessing] = useState(!!isQuotation);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const effectiveRefetchInterval = pullQuotesRefetchInterval || refetchInterval;
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();

  const canViewQuotePricing = permissions && JSON.parse(permissions).permissions.booking.view_quote_pricing;
  const { traffic_light_detail } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};
  const { traffic_light_enabled, traffic_light_margin, traffic_light_freight_modes } = traffic_light_detail ?? {};

  const { data: shipmentData } = useGetShipmentBasic(id, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { data: quotationData, isLoading } = useGetQuotations({
    shipmentId: id,
    queryFn: getQuoteData,
    options: {
      enabled: !!id,
      refetchInterval: effectiveRefetchInterval,
    },
  });
  const approvalPending = quotationData?.quotations?.some((quote: IQuotation) => quote.status === "require_approval");

  const trafficLightEnabled = traffic_light_enabled && quotationData?.quotations?.length > 1;
  const trafficLightBuckets: ITrafficLightBucket = useMemo(() => {
    if (!trafficLightEnabled) return {};
    const output: ITrafficLightBucket = {};
    quotationData?.quotations.forEach((quote: IQuotation) => {
      const {
        service: { freight_mode_label },
        financial: { total_quoted_amount_usd },
        status,
      } = quote;
      if (!traffic_light_freight_modes.includes(freight_mode_label)) return;
      if (["canceled", "canceled_by_provider"].includes(status)) return;

      output[freight_mode_label] = {
        totalCostUSD: (output[freight_mode_label]?.totalCostUSD ?? 0) + Number(total_quoted_amount_usd),
        totalQuotes: (output[freight_mode_label]?.totalQuotes ?? 0) + 1,
      };
    });
    /* eslint-disable array-callback-return */
    Object.entries(output).map(([freightMode, data]) => {
      const { totalCostUSD, totalQuotes } = data;
      const averageCostUSD = totalCostUSD / totalQuotes;
      const upperMarginUSD = averageCostUSD + averageCostUSD * (traffic_light_margin / 100);
      const lowerMarginUSD = averageCostUSD - averageCostUSD * (traffic_light_margin / 100);
      output[freightMode] = {
        ...output[freightMode],
        averageCostUSD,
        upperMarginUSD,
        lowerMarginUSD,
      };
    });
    return output;
  }, [quotationData?.quotations]);

  const data = useMemo(() => {
    if (!quotationData?.quotations) return [];
    if (!showAlternativeQuotes && approvalPending && quotationData.quotations?.length > 1) {
      return quotationData.quotations.filter((quote: IQuotation) => quote.status === "require_approval");
    }
    return quotationData.quotations;
  }, [quotationData?.quotations, approvalPending, showAlternativeQuotes]);

  const settings = useMemo(() => quotationData?.settings || {}, [quotationData?.settings]);

  const urlParams = new URLSearchParams(window.location.search);
  const quotationReference = urlParams.get("quotation_reference");

  const invitedProviders = useMemo(
    () => providers?.filter((provider: IProviderProps) => provider.status === ProviderInviteStatus.INVITED) ?? [],
    [providers],
  );

  const rateCardAvailable =
    invitedProviders.length > 0 && invitedProviders.filter((provider: IProviderProps) => provider.rate_card_available);

  useEffect(() => {
    if (isQuotation && id) {
      setIsProcessing(true);
      setRefetchInterval(10000);
      setTimeout(() => {
        setRefetchInterval(false);
        setIsProcessing(false);
        setIsInitialize(false);
        if (data?.length > 0) {
          setIsQuotation(false);
        }
      }, 50000);
    }
  }, [id]);

  function getQuoteData() {
    return getQuotations(id)
      .then((res) => {
        if (isQuotation && res.data.quotations?.length > 0) {
          setIsProcessing(false);
          setIsLoadingMoreData(true);
        }
        if (isQuotation && res.data.quotations?.length === 0) {
          setIsQuotation(true);
        }
        if (isQuotation) {
          setTimeout(() => {
            setIsLoadingMoreData(false);
          }, 40000);
        }
        const formattedData = formatData(res.data.quotations);
        return Promise.resolve({
          ...res.data,
          quotations: formattedData,
        });
      })
      .catch((err) => {
        if (err.response.status !== 500) errorToast(err);
        return Promise.reject(err);
      });
  }

  function updateQuotationsAvailable(state: GlobalState, payload: any) {
    return {
      ...state,
      quotationsAvailable: payload,
    };
  }

  useEffect(() => {
    if (!data || data.length === 0) actions.updateQuotationsAvailable(false);
    else actions.updateQuotationsAvailable(true);
  }, [data?.length]);

  const formatData = (quoteData: IQuotation[]) => {
    const earliestDeliveryDate = moment.min(
      quoteData
        .filter((item) => item.schedule.quoted_time_delivery_date)
        .map((item) => moment(item.schedule.quoted_time_delivery_date)),
    );
    const earliestPickupDate = moment.min(quoteData.map((item) => moment(item.schedule.quoted_pickup_date)));

    // If ALL pickupdates are the same, then no pickup date is the earliest. Otherwise it sets an earliest key.
    if (!quoteData.every((item) => moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate))) {
      quoteData.forEach((item) => {
        if (moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate)) item.schedule.earliest_pickup = true;
      });
    }

    // If ALL delivery dates are the same, then no delivery date is the earliest. Otherwise it sets an earliest key.
    if (!quoteData.every((item) => moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))) {
      quoteData.forEach((item) => {
        if (moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))
          item.schedule.earliest_delivery = true;
      });
    }

    if (
      !quoteData.every((item) => moment(item.schedule.quoted_pickup_date).isSame(earliestPickupDate)) ||
      !quoteData.every((item) => moment(item.schedule.quoted_time_delivery_date).isSame(earliestDeliveryDate))
    ) {
      quoteData.forEach((item: IQuotation) => {
        const transitTime = moment(item.schedule.quoted_time_delivery_date).diff(
          moment(item.schedule.quoted_pickup_date),
          "hours",
        );
        item.schedule.transit_time = transitTime;
      });
      const shortestTransitTime = Math.min(...quoteData.map((item) => item.schedule.transit_time));
      quoteData.forEach((item) => {
        if (item.schedule.transit_time === shortestTransitTime) item.schedule.shortest_transit_time = true;
      });
    }

    // Set price labels, only if every single quote has the same quote price, it gets no price labels.
    if (!quoteData.every((item) => item.financial.total_quoted_amount === quoteData[0].financial.total_quoted_amount)) {
      quoteData.sort((a: any, b: any) => a.financial.total_quoted_amount - b.financial.total_quoted_amount);

      quoteData.forEach((item: any) => {
        const highestPrice = Math.max(...quoteData.map((item: any) => item.financial.total_quoted_amount));
        const lowestPrice = Math.min(...quoteData.map((item: any) => item.financial.total_quoted_amount));
        if (highestPrice % 1 === 0) item.financial.highest_price = `${highestPrice}.00`;
        if (lowestPrice % 1 === 0) item.financial.lowest_price = `${lowestPrice}.00`;
        if (Number(item.financial.total_quoted_amount) === highestPrice) {
          item.financial.is_highest_price = true;
        }
        if (Number(item.financial.total_quoted_amount) === lowestPrice) {
          item.financial.is_lowest_price = true;
        }
      });
    }
    return quoteData;
  };

  const columns = useMemo(
    () =>
      getQuotesColumns(
        intl,
        settings,
        shipmentData,
        requestStatus,
        id,
        approvalPending,
        () => {
          fetchData();
          queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(id) });
        },
        canViewQuotePricing,
        trafficLightEnabled,
        trafficLightBuckets,
      ),
    [settings, shipmentData, requestStatus, approvalPending],
  );

  const instance = useTable<any>(
    {
      columns,
      data,
      SubQuote,
    },
    useSortBy,
    useColumnOrder,
    useExpanded,
  );

  const fallbackMessage = fallback || "No quotes found.";

  const handleRowClick = (row: any, event: any) => {
    const modalWrapper = document.getElementsByClassName("modal")[0];
    if (modalWrapper && modalWrapper.contains(event.target)) return;
    event.stopPropagation();
    if (event.target.tagName === "BUTTON") return;
    row.toggleRowExpanded();
  };

  useEffect(() => {
    if (quotationReference && instance && !isLoading) {
      const row = instance.rows.find((r) => r.original.reference === quotationReference);
      if (row) row.toggleRowExpanded();
    }
  }, [quotationReference, instance, isLoading]);

  return (
    <>
      {!rateCardAvailable?.length && <RateCardErrors providers={invitedProviders} />}
      <ParcelQuoteErrors providers={invitedProviders} />
      <Row className="ml-0 mr-0" style={{ marginBottom: "16px" }}>
        <Card cardStyle={{ borderRadius: "16px", cursor: "default !important" }} className="quotes-container">
          <DataTable
            customNoResults={fallbackMessage}
            tableInstance={instance}
            isLoading={!isQuotation && isLoading}
            RowSubComponent={SubQuote}
            onRowClick={handleRowClick}
            isProcessing={isProcessing}
            isLoadingMoreData={isLoadingMoreData}
            rowRenderer={(row) =>
              row?.original?.general?.label_creation_error_message && (
                <QuoteError colSpan={row.cells?.length} errorMessage={row.original.general.label_creation_error_message} />
              )
            }
          />
          {approvalPending && quotationData.quotations?.length > 1 && (
            <div
              className="d-flex cursor-pointer pb-2"
              style={{ marginLeft: "2rem" }}
              onClick={() => setShowAlternativeQuotes((prev) => !prev)}
            >
              <EyeIcon />{" "}
              <p className="ml-2">
                {showAlternativeQuotes
                  ? intl.formatMessage({
                      id: "shipment-quotes.components.quotes.hideAlernativeQuote",
                      defaultMessage: "Hide alternative quotes",
                    })
                  : intl.formatMessage({
                      id: "shipment-quotes.components.quotes.showAlernativeQuote",
                      defaultMessage: "Show alternative quotes",
                    })}
              </p>
            </div>
          )}
        </Card>
      </Row>
    </>
  );
};

Quotes.propTypes = {
  id: PropTypes.string.isRequired,
  requestStatus: PropTypes.string.isRequired,
  fetchData: PropTypes.func.isRequired,
  providers: PropTypes.instanceOf(Array),
  pullQuotesRefetchInterval: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  isQuotation: PropTypes.bool,
  setIsQuotation: PropTypes.func,
  setIsInitialize: PropTypes.func,
};

Quotes.defaultProps = {
  isQuotation: false,
  providers: [],
  pullQuotesRefetchInterval: false,
  setIsQuotation: undefined,
  setIsInitialize: undefined,
};

export default Quotes;
